import React from 'react';
import { api } from '../lib/api';
import { Badge, Btn } from './index';
import { formatInTz, zonedDateTimeToUtc, DEFAULT_TIMEZONE } from '../lib/timezone';
import { peso } from '../lib/format';
import { AdminCancelBookingDialog } from './AdminCancelBookingDialog';
import { palette as T, type } from '../design';

// Admin's bookings list (AD-2). Filters and pages run on the server, 50 bookings per page.
// Admin picks days, not instants: a day is read in the admin timezone, so "30 Sep" means
// 30 Sep 00:00 to 1 Oct 00:00 in Manila for every admin, wherever they sit.

const ADMIN_TZ = DEFAULT_TIMEZONE;

// Mirror of BOOKING_STATUS in backend/src/lib/constants.js. The words show in the UI verbatim.
export const BOOKING_STATUSES = ['pending', 'confirmed', 'cancelled', 'completed'];
const STATUS_BADGE = { pending: 'gold', confirmed: 'sage' };
// Admin cancels only a booking that still holds a seat (AD-3.5): cancelled is final, completed is taught.
const CANCELLABLE_STATUSES = ['pending', 'confirmed'];

// What admin reads after a cancel. A failed email is said plainly: the other side may not know yet.
export function cancelledNotice({ emailsDelivered }) {
  return emailsDelivered
    ? { tone: 'ok', text: 'Booking cancelled. The seat is free, and the student and the instructor each got an email with your reason.' }
    : { tone: 'warn', text: 'Booking cancelled and the seat is free, but an email did not send. Tell the student and the instructor yourself. The Error Log has the detail.' };
}

const EMPTY_FILTERS = { status: '', instructorId: '', fromDay: '', toDay: '' };

const FONT = "'Plus Jakarta Sans', sans-serif";
const fieldStyle = { padding: '10px 12px', borderRadius: 10, border: '1px solid ' + T.border, fontFamily: FONT, fontSize: type.body, outline: 'none', background: T.white, width: '100%', boxSizing: 'border-box' };
const labelStyle = { display: 'flex', flexDirection: 'column', gap: 6, flex: '1 1 160px', minWidth: 0, fontFamily: FONT, fontSize: type.label, fontWeight: 600, color: T.walnut };
const cellStyle = { padding: '12px 16px', fontFamily: FONT, fontSize: type.body, color: T.walnut };
const noteStyle = { fontFamily: FONT, fontSize: type.body, color: T.driftwood, margin: 0 };

// "2026-09-30" -> "2026-10-01". Calendar arithmetic only, so no timezone is involved.
function nextDay(dayStr) {
  const [y, m, d] = dayStr.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
}

// The query string for one page of the list. The end day counts in full, so `to` is the
// start of the day after it (the server treats `to` as exclusive).
export function bookingsQuery(filters, page, tz = ADMIN_TZ) {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.instructorId) params.set('instructorId', filters.instructorId);
  if (filters.fromDay) params.set('from', zonedDateTimeToUtc(filters.fromDay, '00:00', tz).toISOString());
  if (filters.toDay) params.set('to', zonedDateTimeToUtc(nextDay(filters.toDay), '00:00', tz).toISOString());
  params.set('page', String(page));
  return params.toString();
}

export function AdminBookingsPanel({ instructors = [] }) {
  const [filters, setFilters] = React.useState(EMPTY_FILTERS);
  const [page, setPage] = React.useState(1);
  const [result, setResult] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [reloadKey, setReloadKey] = React.useState(0);
  const [cancelling, setCancelling] = React.useState(null);
  const [notice, setNotice] = React.useState(null);
  const idBase = React.useId();
  const ids = { status: idBase + 'status', instructor: idBase + 'instructor', from: idBase + 'from', to: idBase + 'to' };

  const rangeBackwards = Boolean(filters.fromDay && filters.toDay && filters.toDay < filters.fromDay);

  React.useEffect(() => {
    if (rangeBackwards) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.get('/admin/bookings?' + bookingsQuery(filters, page))
      .then((res) => { if (!cancelled) setResult(res); })
      .catch((err) => {
        console.error('Admin bookings fetch failed:', err);
        if (!cancelled) setError(err?.data?.message || 'We could not load bookings. Check your connection, then try again.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [filters, page, rangeBackwards, reloadKey]);

  // A new filter starts again at page 1: the old page number may not exist any more.
  const setFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setPage(1);
  };

  // The cancel is saved: close the dialog, say what happened, and reload the page of bookings.
  const handleCancelled = (result) => {
    setCancelling(null);
    setNotice(cancelledNotice(result || {}));
    setReloadKey((k) => k + 1);
  };

  const bookings = result?.bookings || [];
  const total = result?.total || 0;
  const pageSize = result?.pageSize || 50;
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const firstRow = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const lastRow = Math.min(page * pageSize, total);
  const filtered = Object.values(filters).some(Boolean);

  return (
    <div>
      <div style={{ background: T.white, borderRadius: 20, border: '1px solid ' + T.border, padding: 20, marginBottom: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
          <div style={labelStyle}>
            <label htmlFor={ids.status}>Status</label>
            <select id={ids.status} value={filters.status} onChange={(e) => setFilter('status', e.target.value)} style={fieldStyle}>
              <option value="">All statuses</option>
              {BOOKING_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div style={labelStyle}>
            <label htmlFor={ids.instructor}>Instructor</label>
            <select id={ids.instructor} value={filters.instructorId} onChange={(e) => setFilter('instructorId', e.target.value)} style={fieldStyle}>
              <option value="">All instructors</option>
              {instructors.map((ins) => <option key={ins.id} value={ins.id}>{ins.displayName || ins.name}</option>)}
            </select>
          </div>
          <div style={labelStyle}>
            <label htmlFor={ids.from}>Session date from</label>
            <input id={ids.from} type="date" value={filters.fromDay} onChange={(e) => setFilter('fromDay', e.target.value)} style={fieldStyle} />
          </div>
          <div style={labelStyle}>
            <label htmlFor={ids.to}>Session date to</label>
            <input id={ids.to} type="date" value={filters.toDay} onChange={(e) => setFilter('toDay', e.target.value)} style={fieldStyle} />
          </div>
          {filtered && <Btn size="sm" variant="subtle" onClick={clearFilters}>Clear filters</Btn>}
        </div>
        <p style={{ ...noteStyle, fontSize: type.label, marginTop: 10 }}>Days are in {ADMIN_TZ} time. Newest session first.</p>
        {rangeBackwards && (
          <p role="alert" style={{ ...noteStyle, color: T.errorRed, marginTop: 8 }}>The end day is before the start day. Pick an end day on or after the start day.</p>
        )}
      </div>

      {error && !rangeBackwards && (
        <div role="alert" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, background: T.white, borderRadius: 16, border: '1px solid ' + T.errorRed, padding: 16, marginBottom: 16 }}>
          <p style={{ ...noteStyle, color: T.errorRed, flex: '1 1 200px' }}>{error}</p>
          <Btn size="sm" variant="ghost" onClick={() => setReloadKey((k) => k + 1)}>Try again</Btn>
        </div>
      )}

      {notice && (
        <div role="status" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, background: T.white, borderRadius: 16, border: '1px solid ' + (notice.tone === 'ok' ? T.sageGreen : T.errorRed), padding: 16, marginBottom: 16 }}>
          <p style={{ ...noteStyle, color: notice.tone === 'ok' ? T.espresso : T.errorRed, flex: '1 1 200px' }}>{notice.text}</p>
          <Btn size="sm" variant="subtle" onClick={() => setNotice(null)}>Dismiss</Btn>
        </div>
      )}

      <div style={{ background: T.white, borderRadius: 20, border: '1px solid ' + T.border, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
            <thead>
              <tr style={{ background: T.ivory }}>
                {['Student', 'Instructor', 'Session', 'Session date', 'Status', 'Price', 'Action'].map((h) => (
                  <th key={h} style={{ fontFamily: FONT, fontWeight: 700, fontSize: type.caption, color: T.driftwood, textTransform: 'uppercase', letterSpacing: 0.8, padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid ' + T.border }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bookings.map((b) => (
                <tr key={b.id} style={{ borderBottom: '1px solid ' + T.border }}>
                  <td style={{ ...cellStyle, fontWeight: 600, color: T.espresso }}>{b.studentName}</td>
                  <td style={cellStyle}>{b.instructorName}</td>
                  <td style={cellStyle}>{b.session?.title || b.sessionType}</td>
                  <td style={{ ...cellStyle, fontSize: type.label, color: T.driftwood }}>{formatInTz(b.scheduledAt, ADMIN_TZ)}</td>
                  <td style={cellStyle}><Badge variant={STATUS_BADGE[b.status] || 'subtle'}>{b.status}</Badge></td>
                  <td style={cellStyle}>{peso(b.price)}</td>
                  <td style={cellStyle}>
                    {CANCELLABLE_STATUSES.includes(b.status) && (
                      <Btn size="sm" variant="ghost" onClick={() => { setNotice(null); setCancelling(b); }}>Cancel</Btn>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && !error && bookings.length === 0 && (
          <p style={{ ...noteStyle, fontSize: type.body, textAlign: 'center', padding: '40px 16px' }}>
            {filtered ? 'No bookings match these filters. Change a filter or clear them.' : 'No bookings yet.'}
          </p>
        )}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 16 }}>
        <p style={noteStyle} aria-live="polite">
          {loading ? 'Loading bookings…' : total > 0 ? `Showing ${firstRow}–${lastRow} of ${total} bookings` : ''}
        </p>
        {total > pageSize && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Btn size="sm" variant="subtle" onClick={() => setPage((p) => p - 1)} disabled={loading || page <= 1}>Previous</Btn>
            <span style={{ ...noteStyle, color: T.walnut }}>Page {page} of {lastPage}</span>
            <Btn size="sm" variant="subtle" onClick={() => setPage((p) => p + 1)} disabled={loading || page >= lastPage}>Next</Btn>
          </div>
        )}
      </div>

      {cancelling && (
        <AdminCancelBookingDialog booking={cancelling} onCancelled={handleCancelled} onClose={() => setCancelling(null)} />
      )}
    </div>
  );
}
